import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { K2Logo } from '@/components/brand/K2Logo';
import { Text } from '@/components/ui/Text';
import { ApiError, NetworkError, login } from '@/sync/client';
import { DEFAULT_BASE_URL } from '@/sync/credentials';
import { tick } from '@/sync/live';
import { colors } from '@/theme/tokens';
import styles from './Login.module.css';

/**
 * The gate. Nothing else in the app renders until somebody is signed in.
 *
 * There is no device to enrol and no server address to type: a phone and a
 * laptop load the same web app from the same host, and the only thing worth
 * knowing is which person is using it and which shop they belong to.
 */
export function Login() {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [shops, setShops] = useState<{ code: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (shopCode?: string) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await login(DEFAULT_BASE_URL, phone.trim(), password, shopCode);
      if ('shops' in result) {
        // They work in more than one shop and named none. Asking beats
        // guessing: the wrong guess shows them another shop's takings.
        setShops(result.shops);
        return;
      }
      setPassword('');
      // Pulls the shop's state straight away, so the app is populated by the
      // time the gate comes down rather than flashing empty screens first.
      await tick();
    } catch (cause) {
      if (cause instanceof ApiError) setError(cause.messageLo);
      else if (cause instanceof NetworkError) setError('ຕໍ່ອິນເຕີເນັດບໍ່ໄດ້ ກວດເບິ່ງສັນຍານ');
      else setError('ເຂົ້າສູ່ລະບົບບໍ່ສຳເລັດ ລອງໃໝ່');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.screen}>
      <div className={styles.brand}>
        <K2Logo size={56} />
        <Text weight={800} size={22} color={colors.navy}>
          K2 POS
        </Text>
        <Text size={13} color={colors.textMuted}>
          ລະບົບຂາຍ ສຳລັບຮ້ານເຄື່ອງກໍ່ສ້າງ
        </Text>
      </div>

      <Card className={styles.card}>
        {shops.length > 0 ? (
          <>
            <Text size={15} weight={700}>
              ເລືອກຮ້ານ
            </Text>
            <Text size={12.5} color={colors.textMuted}>
              ບັນຊີນີ້ມີສິດຫຼາຍກວ່າໜຶ່ງຮ້ານ
            </Text>
            {shops.map((shop) => (
              <Button
                key={shop.code}
                label={shop.name}
                variant="outline"
                disabled={busy}
                onPress={() => void submit(shop.code)}
              />
            ))}
          </>
        ) : (
          <>
            <div className={styles.field}>
              <Text size={12.5} color={colors.textMuted}>
                ເບີໂທ
              </Text>
              <Field
                value={phone}
                onChange={setPhone}
                kind="tel"
                placeholder="020 55 512 345"
                autoFocus
              />
            </div>

            <div className={styles.field}>
              <Text size={12.5} color={colors.textMuted}>
                ລະຫັດຜ່ານ
              </Text>
              <Field
                value={password}
                onChange={setPassword}
                secret
                placeholder="••••••••"
                onEnter={() => void submit()}
              />
            </div>

            <Button
              label={busy ? 'ກຳລັງເຂົ້າ…' : 'ເຂົ້າສູ່ລະບົບ'}
              disabled={busy || !phone.trim() || !password}
              onPress={() => void submit()}
            />
          </>
        )}

        {error ? (
          <Text size={12.5} color={colors.skyDeep}>
            {error}
          </Text>
        ) : null}
      </Card>

      <Text size={11.5} color={colors.textFaint} align="center">
        ລືມລະຫັດຜ່ານ? ຕິດຕໍ່ K2TECH
      </Text>
    </div>
  );
}
